/**
 * State shape for the persistent `affine:agent-stages` block. Serialized
 * as JSON into the block's `stagesJson` prop so a collapsed research log
 * can be fully rehydrated after a doc reload.
 *
 * Only coarse state lives here — ephemeral UI ticks (per-second elapsed
 * timer, active sub-step typewriter) stay as Lit `@state()` and never
 * reach the Y.Doc. This bounds CRDT update volume during streaming.
 */

import type { ResearchMode, StepIcon, ToolUseSource } from '../types';

export const AGENT_RUN_STATE_VERSION = 1;

export const SYNTHESIS_STAGE_ID = '__synthesis__';

export type StageStatus = 'pending' | 'active' | 'done' | 'error';

export interface StageEntry {
  id: string;
  icon: StepIcon;
  title: string;
  status: StageStatus;
  startedAt: number;
  endedAt?: number;
  lastSubStep?: string;
  subStepDetail?: string;
  sources: ToolUseSource[];
  citationsFound?: number;
  error?: string;
  agentType?: string;
}

export interface AgentRunState {
  version: typeof AGENT_RUN_STATE_VERSION;
  planId?: string;
  mode?: ResearchMode;
  query: string;
  subQuestions: string[];
  stages: StageEntry[];
  startedAt: number;
  endedAt?: number;
  streaming: boolean;
  collapsed: boolean;
  errorMessage?: string;
  agentsSeen: string[];
  totalCitations: number;
}

export function createEmptyAgentRunState(
  query: string,
  now: number = Date.now()
): AgentRunState {
  return {
    version: AGENT_RUN_STATE_VERSION,
    query,
    subQuestions: [],
    stages: [],
    startedAt: now,
    streaming: true,
    collapsed: false,
    agentsSeen: [],
    totalCitations: 0,
  };
}

export function serialiseAgentRunState(state: AgentRunState): string {
  return JSON.stringify(state);
}

export function deserialiseAgentRunState(
  json: string | null | undefined
): AgentRunState | null {
  if (!json) return null;
  try {
    const parsed = JSON.parse(json) as Partial<AgentRunState>;
    if (parsed?.version !== AGENT_RUN_STATE_VERSION) return null;
    return parsed as AgentRunState;
  } catch {
    return null;
  }
}
